/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, May 11th 2023, 1:15:53 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { APISERVICEPATHS, useAPIForm, useAPIRequest } from "lib/api";
import {
  AIAgentConfiguration,
  AIAgentConfigurationValidationSchema,
  AIFormReviewAgent,
  AIFormReviewAgentValidationSchema,
} from "lib/schemas";
import { useDeleteOSItem } from "version-control/services";

const aiAgentConfigurationsAPIPath = APISERVICEPATHS.aiAgentConfigurations;

const useDeleteAIAgentConfigurations = () => useDeleteOSItem<AIAgentConfiguration>(aiAgentConfigurationsAPIPath);

interface UseAIAgentConfigurationsAPIFormAPIFormSpec {
  aiAgentConfigurationFetchId: AIAgentConfiguration["id"];
  aiAgentConfigurationDefaultValues: AIAgentConfiguration;
  updateAIAgentConfigurationFromAPICallback: (aiAgentConfigurations: AIAgentConfiguration) => void;
}

const useAIAgentConfigurationsAPIForm = ({
  aiAgentConfigurationFetchId,
  aiAgentConfigurationDefaultValues,
  updateAIAgentConfigurationFromAPICallback,
}: UseAIAgentConfigurationsAPIFormAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm<AIAgentConfiguration>({
    apiServicePath: aiAgentConfigurationsAPIPath,
    apiFormDataValidationSchema: AIAgentConfigurationValidationSchema,
    apiFormDefaultValues: aiAgentConfigurationDefaultValues,
    apiInitialFetchId: aiAgentConfigurationFetchId,
    primaryKey: "id",
    setAPIFormResponse: updateAIAgentConfigurationFromAPICallback,
  });

  return useAPIFormReturn;
};

const aiFormReviewAgentsAPIPath = APISERVICEPATHS.aiFormReviewAgents;

const useDeleteAIFormReviewAgents = () => useDeleteOSItem<AIAgentConfiguration>(aiFormReviewAgentsAPIPath);

interface UseAIFormReviewAgentAPIFormAPIFormSpec {
  aiFormReviewAgentFetchId: AIFormReviewAgent["id"];
  aiFormReviewAgentDefaultValues: AIFormReviewAgent;
  updateAIFormReviewAgentFromAPICallback: (agent: AIFormReviewAgent) => void;
}

const useAIFormReviewAgentsAPIForm = ({
  aiFormReviewAgentFetchId,
  aiFormReviewAgentDefaultValues,
  updateAIFormReviewAgentFromAPICallback,
}: UseAIFormReviewAgentAPIFormAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm<AIFormReviewAgent>({
    apiServicePath: aiFormReviewAgentsAPIPath,
    apiFormDataValidationSchema: AIFormReviewAgentValidationSchema,
    apiFormDefaultValues: aiFormReviewAgentDefaultValues,
    apiInitialFetchId: aiFormReviewAgentFetchId,
    primaryKey: "id",
    setAPIFormResponse: updateAIFormReviewAgentFromAPICallback,
  });

  return useAPIFormReturn;
};

const useAIFormReviewAgentAPI = <T>() => {
  return useAPIRequest<T, AIFormReviewAgent, AIFormReviewAgent>({
    apiServicePath: aiFormReviewAgentsAPIPath,
    makeRequestOnInitialMount: false,
  });
};

export const useAIWorkersAPI = () => ({
  useAIAgentConfigurationsAPIForm,
  useDeleteAIAgentConfigurations,
  useAIFormReviewAgentsAPIForm,
  useDeleteAIFormReviewAgents,
  useAIFormReviewAgentAPI,
});
